import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const TaskForm = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  onSwitchToEdit,
  task = null,
  users,
  departments,
  groups,
  types,
  viewOnly = false
}) => {
  // State for task data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    due_date: '',
    assigned_user_id: '',
    department_id: '',
    group_id: '',
    type_id: '',
    is_completed: false,
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_end_date: '',
    update_series: false
  });
  
  // State for edit mode - start in view mode if viewOnly is true
  const [isEditMode, setIsEditMode] = useState(!viewOnly);
  
  // When editing, pre-fill the form with task data
  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name || '',
        description: task.description || '',
        due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
        assigned_user_id: task.assigned_user_id || '',
        department_id: task.department_id || '',
        group_id: task.group_id || '',
        type_id: task.type_id || '',
        is_completed: task.is_completed || false,
        is_recurring: task.is_recurring || false,
        recurrence_pattern: task.recurrence_pattern || 'weekly',
        recurrence_end_date: task.recurrence_end_date || '',
        update_series: false
      });
    } else {
      // Default values for new task
      setFormData({
        name: '',
        description: '',
        due_date: '',
        assigned_user_id: '',
        department_id: '',
        group_id: '',
        type_id: '',
        is_completed: false,
        is_recurring: false,
        recurrence_pattern: 'weekly',
        recurrence_end_date: '',
        update_series: false
      });
    }
  }, [task]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // When switching from view to edit, make sure we're not accidentally submitting
  const handleSwitchToEdit = (e) => {
    e.preventDefault(); // Prevent form submission
    setIsEditMode(true);
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Don't submit if in view mode
    if (!isEditMode) {
      return;
    }
    
    // Basic validation
    if (!formData.name.trim()) {
      alert('Task name is required');
      return;
    }
    
    // Additional validation for recurring tasks
    if (formData.is_recurring) {
      if (!formData.due_date.trim()) {
        alert('Due date is required for recurring tasks');
        return;
      }
      
      if (!formData.recurrence_end_date.trim()) {
        alert('Recurrence end date is required for recurring tasks');
        return;
      }
      
      // Ensure end date is after start date
      const startDate = new Date(formData.due_date);
      const endDate = new Date(formData.recurrence_end_date);
      
      if (endDate <= startDate) {
        alert('Recurrence end date must be after the due date');
        return;
      }
    }
    
    // Create a copy of form data for submission
    const submitData = { ...formData };
    
    // Ensure due date is properly formatted with timezone handling
    console.log('Due date before formatting:', submitData.due_date, typeof submitData.due_date);
    
    if (submitData.due_date && submitData.due_date.trim() !== '') {
      // Parse the date and adjust for timezone
      // The date input returns YYYY-MM-DD format in local timezone
      // We need to preserve the exact date regardless of timezone
      const parts = submitData.due_date.split('-');
      if (parts.length === 3) {
        // Create date at noon to avoid timezone issues
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
        const day = parseInt(parts[2], 10);
        
        // Create a date at noon to avoid timezone issues
        const date = new Date(year, month, day, 12, 0, 0);
        
        // Format to ISO string but only keep the date portion
        submitData.due_date = date.toISOString().split('T')[0];
        console.log('Formatted date for submission:', submitData.due_date);
      } else {
        console.log('Using provided due date:', submitData.due_date);
      }
    } else {
      // If due_date is empty or just whitespace, set it to null explicitly
      submitData.due_date = null;
      console.log('Setting due date to null');
    }
    
    // Format recurrence end date the same way
    if (submitData.recurrence_end_date && submitData.recurrence_end_date.trim() !== '') {
      const parts = submitData.recurrence_end_date.split('-');
      if (parts.length === 3) {
        // Create date at noon to avoid timezone issues
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
        const day = parseInt(parts[2], 10);
        
        // Create a date at noon to avoid timezone issues
        const date = new Date(year, month, day, 12, 0, 0);
        
        // Format to ISO string but only keep the date portion
        submitData.recurrence_end_date = date.toISOString().split('T')[0];
        console.log('Formatted end date for submission:', submitData.recurrence_end_date);
      }
    } else {
      // If recurrence_end_date is empty or just whitespace, set it to null explicitly
      submitData.recurrence_end_date = null;
    }
    
    // Make sure is_recurring is properly set
    submitData.is_recurring = !!submitData.is_recurring;
    
    // If is_recurring is false, don't send recurrence_pattern and recurrence_end_date
    if (!submitData.is_recurring) {
      submitData.recurrence_pattern = null;
      submitData.recurrence_end_date = null;
    }
    
    // Execute the submit callback with the form data
    onSubmit(submitData);
  };
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gray-800 p-6 text-left align-middle shadow-xl transition-all border border-gray-700">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-100 flex justify-between items-center"
                >
                  <span>
                    {task 
                      ? (isEditMode ? 'Edit Task' : 'View Task') 
                      : 'Add New Task'
                    }
                  </span>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-300"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </Dialog.Title>
                
                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                  {/* Task Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                      Task Name {isEditMode && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required={isEditMode}
                      readOnly={!isEditMode}
                      disabled={!isEditMode}
                      className={`mt-1 block w-full rounded-lg ${!isEditMode ? 'bg-gray-900 border border-gray-700 cursor-default' : 'bg-gray-700 border-gray-600'} text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm`}
                      placeholder={isEditMode ? "Enter task name" : ""}
                    />
                  </div>
                  
                  {/* Task Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-300">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      readOnly={!isEditMode}
                      disabled={!isEditMode}
                      className={`mt-1 block w-full rounded-lg ${!isEditMode ? 'bg-gray-900 border border-gray-700 cursor-default' : 'bg-gray-700 border-gray-600'} text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm`}
                      placeholder={isEditMode ? "Enter task description" : ""}
                    />
                  </div>
                  
                  {/* Due Date */}
                  <div>
                    <label htmlFor="due_date" className="block text-sm font-medium text-gray-300">
                      Due Date
                    </label>
                    {isEditMode ? (
                      <input
                        type="date"
                        id="due_date"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    ) : (
                      <div className={`mt-1 py-2 px-3 block w-full rounded-lg ${!isEditMode ? 'bg-gray-900 text-white border border-gray-700' : 'bg-gray-700 text-white'} sm:text-sm`}>
                        {formData.due_date ? new Date(formData.due_date).toLocaleDateString() : 'Not set'}
                      </div>
                    )}
                  </div>
                  
                  {/* Assigned User */}
                  <div>
                    <label htmlFor="assigned_user_id" className="block text-sm font-medium text-gray-300">
                      Assigned To
                    </label>
                    {isEditMode ? (
                      <select
                        id="assigned_user_id"
                        name="assigned_user_id"
                        value={formData.assigned_user_id}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">Unassigned</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className={`mt-1 py-2 px-3 block w-full rounded-lg ${!isEditMode ? 'bg-gray-900 text-white border border-gray-700' : 'bg-gray-700 text-white'} sm:text-sm`}>
                        {formData.assigned_user_id
                          ? users.find(u => u.id == formData.assigned_user_id)?.name || 'Unknown User'
                          : 'Unassigned'
                        }
                      </div>
                    )}
                  </div>
                  
                  {/* Two-column layout for categories */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Department */}
                    <div>
                      <label htmlFor="department_id" className="block text-sm font-medium text-gray-300">
                        Department
                      </label>
                      {isEditMode ? (
                        <select
                          id="department_id"
                          name="department_id"
                          value={formData.department_id}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">None</option>
                          {departments.map((department) => (
                            <option key={department.id} value={department.id}>
                              {department.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className={`mt-1 py-2 px-3 block w-full rounded-lg ${!isEditMode ? 'bg-gray-900 text-white border border-gray-700' : 'bg-gray-700 text-white'} sm:text-sm`}>
                          {formData.department_id
                            ? departments.find(d => d.id == formData.department_id)?.name || 'Unknown Department'
                            : 'None'
                          }
                        </div>
                      )}
                    </div>
                    
                    {/* Group */}
                    <div>
                      <label htmlFor="group_id" className="block text-sm font-medium text-gray-300">
                        Group
                      </label>
                      {isEditMode ? (
                        <select
                          id="group_id"
                          name="group_id"
                          value={formData.group_id}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">None</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className={`mt-1 py-2 px-3 block w-full rounded-lg ${!isEditMode ? 'bg-gray-900 text-white border border-gray-700' : 'bg-gray-700 text-white'} sm:text-sm`}>
                          {formData.group_id
                            ? groups.find(g => g.id == formData.group_id)?.name || 'Unknown Group'
                            : 'None'
                          }
                        </div>
                      )}
                    </div>
                    
                    {/* Type */}
                    <div>
                      <label htmlFor="type_id" className="block text-sm font-medium text-gray-300">
                        Type
                      </label>
                      {isEditMode ? (
                        <select
                          id="type_id"
                          name="type_id"
                          value={formData.type_id}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">None</option>
                          {types.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className={`mt-1 py-2 px-3 block w-full rounded-lg ${!isEditMode ? 'bg-gray-900 text-white border border-gray-700' : 'bg-gray-700 text-white'} sm:text-sm`}>
                          {formData.type_id
                            ? types.find(t => t.id == formData.type_id)?.name || 'Unknown Type'
                            : 'None'
                          }
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Status Information */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Status</h4>
                    {isEditMode ? (
                      <>
                        {/* Is Completed Checkbox - Edit Mode */}
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="is_completed"
                            name="is_completed"
                            checked={formData.is_completed}
                            onChange={handleChange}
                            className="h-4 w-4 rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 bg-gray-700"
                          />
                          <label htmlFor="is_completed" className="ml-2 block text-sm text-gray-300">
                            Mark as completed
                          </label>
                        </div>
                      </>
                    ) : (
                      /* Status Display - View Mode */
                      <div className="mt-1 py-2 px-3 block w-full rounded-lg bg-gray-900 text-white border border-gray-700 sm:text-sm">
                        <div className="flex flex-wrap gap-2">
                          {formData.is_completed ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-900/25 text-green-400 border border-green-700">
                              Completed
                            </span>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-900/25 text-blue-400 border border-blue-700">
                              Active
                            </span>
                          )}
                          {formData.is_recurring && (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-900/25 text-purple-400 border border-purple-700">
                              {formData.recurrence_pattern || 'Recurring'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Is Recurring */}
                  {isEditMode && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_recurring"
                        name="is_recurring"
                        checked={formData.is_recurring}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 bg-gray-700"
                        disabled={task !== null} // Disable for existing tasks
                      />
                      <label htmlFor="is_recurring" className="ml-2 block text-sm text-gray-300">
                        Recurring task
                        {task !== null && (
                          <span className="ml-2 text-xs text-gray-400">
                            (Can't modify after creation)
                          </span>
                        )}
                      </label>
                    </div>
                  )}
                  
                  {/* Recurrence Pattern (show only if is_recurring is checked) */}
                  {isEditMode && formData.is_recurring && (
                    <div>
                      <label htmlFor="recurrence_pattern" className="block text-sm font-medium text-gray-300">
                        Recurrence Pattern
                      </label>
                      <select
                        id="recurrence_pattern"
                        name="recurrence_pattern"
                        value={formData.recurrence_pattern}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        disabled={task !== null} // Disable for existing tasks
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annual">Annual</option>
                      </select>
                    </div>
                  )}
                  
                  {/* Recurrence End Date (show only if is_recurring is checked) */}
                  {isEditMode && formData.is_recurring && (
                    <div>
                      <label htmlFor="recurrence_end_date" className="block text-sm font-medium text-gray-300">
                        Recurrence End Date <span className="text-xs text-gray-400">(Required for recurring tasks)</span>
                      </label>
                      <input
                        type="date"
                        id="recurrence_end_date"
                        name="recurrence_end_date"
                        value={formData.recurrence_end_date}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        disabled={task !== null} // Disable for existing tasks
                        required={formData.is_recurring}
                      />
                      <p className="mt-1 text-xs text-gray-400">
                        No task instances will be created after this date
                      </p>
                    </div>
                  )}
                  
                  {/* Update Series (show only when editing a recurring task) */}
                  {isEditMode && task && task.is_recurring && task.recurring_series_id && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="update_series"
                        name="update_series"
                        checked={formData.update_series}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-600 text-indigo-600 focus:ring-indigo-500 bg-gray-700"
                      />
                      <label htmlFor="update_series" className="ml-2 block text-sm text-gray-300">
                        Update all tasks in this recurring series
                        <span className="block text-xs text-gray-400">
                          (Only name, description, and assignments will be updated, not dates)
                        </span>
                      </label>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex justify-between mt-6 px-4 py-3 bg-gray-800 sm:px-6 rounded-b-lg">
                    <div>
                      {!isEditMode && task && (
                        <button
                          type="button"
                          onClick={() => {
                            if (onSwitchToEdit) {
                              onSwitchToEdit(task || formData);
                              setIsEditMode(true);
                            } else {
                              setIsEditMode(true);
                            }
                          }}
                          className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          Edit Task
                        </button>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-gray-700 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        {isEditMode ? "Cancel" : "Close"}
                      </button>
                      
                      {isEditMode && (
                        <button
                          type="submit"
                          className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          {task ? "Save Changes" : "Create Task"}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TaskForm; 